import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Trash2, Edit2, Eye, RefreshCw, Save, ExternalLink } from 'lucide-react';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

// Blogbeheer voor de platform-admin. Restaurant-eigenaren hebben hier geen
// toegang toe: alle endpoints hangen achter get_current_admin.
const EMPTY = {
  title: '',
  slug: '',
  language: 'nl',
  excerpt: '',
  meta_description: '',
  content_markdown: '',
  featured_image: '',
  category: '',
  tags: [],
  status: 'draft',
  featured: false,
  published_at: '',
  author: 'FoodFlows',
};

export const AdminBlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null = lijst, anders het formulier
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await adminAPI.getBlogPosts();
      setPosts(data.posts || []);
    } catch (e) {
      toast.error('Kon artikelen niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openNew = () => { setEditing('new'); setForm(EMPTY); setShowPreview(false); };

  const openEdit = async (id) => {
    try {
      const { data } = await adminAPI.getBlogPost(id);
      setEditing(id);
      setShowPreview(false);
      setForm({
        title: data.title || '',
        slug: data.slug || '',
        language: data.language || 'nl',
        excerpt: data.excerpt || '',
        meta_description: data.meta_description || '',
        content_markdown: data.content_markdown || '',
        featured_image: data.featured_image || '',
        category: data.category || '',
        tags: data.tags || [],
        status: data.status || 'draft',
        featured: !!data.featured,
        published_at: (data.published_at || '').slice(0, 10),
        author: data.author || 'FoodFlows',
      });
    } catch (e) {
      toast.error('Kon artikel niet laden');
    }
  };

  const save = async (statusOverride) => {
    if (!form.title.trim()) { toast.error('Titel is verplicht'); return; }
    setSaving(true);
    const payload = {
      ...form,
      status: statusOverride || form.status,
      published_at: form.published_at ? new Date(`${form.published_at}T09:00:00`).toISOString() : null,
      tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(',').map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editing === 'new') {
        await adminAPI.createBlogPost(payload);
        toast.success(payload.status === 'published' ? 'Artikel gepubliceerd!' : 'Concept opgeslagen');
      } else {
        await adminAPI.updateBlogPost(editing, payload);
        toast.success(payload.status === 'published' ? 'Artikel gepubliceerd!' : 'Concept opgeslagen');
      }
      setEditing(null);
      await fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`"${post.title}" definitief verwijderen?`)) return;
    try {
      await adminAPI.deleteBlogPost(post.id);
      toast.success('Artikel verwijderd');
      await fetchPosts();
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  // ---------- Formulier ----------
  if (editing) {
    const publicUrl = `https://food-flows.com/blog/${form.language}/${form.slug || '...'}`;
    return (
      <div className="space-y-6" data-testid="admin-blog-editor">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {editing === 'new' ? 'Nieuw artikel' : 'Artikel bewerken'}
            </h1>
            <p className="text-gray-400 text-sm mt-1 font-mono">{publicUrl}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(null)}
                    className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm">
              Annuleren
            </button>
            <button onClick={() => save('draft')} disabled={saving}
                    data-testid="save-draft-btn"
                    className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
              <Save className="w-4 h-4" /> Opslaan als concept
            </button>
            <button onClick={() => save('published')} disabled={saving}
                    data-testid="publish-btn"
                    className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
              <Eye className="w-4 h-4" /> Publiceren
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Titel">
              <input value={form.title} data-testid="blog-title"
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     className={inputCls} placeholder="Bijv. Zo bespaar je op commissie" />
            </Field>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-400">Inhoud (Markdown)</label>
              <button onClick={() => setShowPreview(!showPreview)}
                      className="text-xs text-gray-400 hover:text-white underline">
                {showPreview ? 'Terug naar bewerken' : 'Voorbeeld'}
              </button>
            </div>
            {showPreview ? (
              <div className="bg-white rounded-lg p-6 min-h-[400px] blog-content"
                   data-testid="blog-md-preview"
                   dangerouslySetInnerHTML={{ __html: simpleMarkdownPreview(form.content_markdown) }} />
            ) : (
              <textarea
                value={form.content_markdown}
                onChange={(e) => setForm({ ...form, content_markdown: e.target.value })}
                rows={22}
                data-testid="blog-markdown"
                className={`${inputCls} font-mono text-sm leading-relaxed`}
                placeholder={'## Kop\n\nTekst met **vet** en [een link](https://food-flows.com).\n\n- punt een\n- punt twee\n\n| Kolom | Waarde |\n|---|---|\n| A | 1 |'}
              />
            )}
            <p className="text-xs text-gray-500">
              Ondersteunt koppen, vet/cursief, lijsten, links, tabellen en citaten.
              De definitieve opmaak wordt server-side gemaakt; dit voorbeeld is indicatief.
            </p>
          </div>

          <div className="space-y-4">
            <Field label="Taal">
              <select value={form.language} data-testid="blog-language"
                      onChange={(e) => setForm({ ...form, language: e.target.value })}
                      className={inputCls}>
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
              </select>
            </Field>
            <Field label="URL (leeg = uit titel)">
              <input value={form.slug} data-testid="blog-slug"
                     onChange={(e) => setForm({ ...form, slug: e.target.value })}
                     className={inputCls} placeholder="zo-bespaar-je-op-commissie" />
            </Field>
            <Field label="Samenvatting (op de overzichtspagina)">
              <textarea value={form.excerpt} rows={3}
                        onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                        className={inputCls} />
            </Field>
            <Field label="Meta-beschrijving (Google, max ~155 tekens)">
              <textarea value={form.meta_description} rows={3} maxLength={200}
                        data-testid="blog-meta-description"
                        onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                        className={inputCls} />
              <span className="text-xs text-gray-500">{(form.meta_description || '').length}/155</span>
            </Field>
            <Field label="Uitgelichte afbeelding (URL)">
              <input value={form.featured_image}
                     onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                     className={inputCls} placeholder="https://..." />
              {form.featured_image && (
                <img src={form.featured_image} alt="" className="mt-2 rounded-lg w-full h-28 object-cover" />
              )}
            </Field>
            <Field label="Categorie">
              <input value={form.category}
                     onChange={(e) => setForm({ ...form, category: e.target.value })}
                     className={inputCls} placeholder="Bijv. Kosten" />
            </Field>
            <Field label="Tags (komma-gescheiden)">
              <input value={Array.isArray(form.tags) ? form.tags.join(', ') : form.tags}
                     onChange={(e) => setForm({ ...form, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                     className={inputCls} placeholder="commissie, prijzen" />
            </Field>
            <Field label="Uitgelicht">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={!!form.featured}
                       onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                       data-testid="blog-featured"
                       className="w-4 h-4 rounded border-gray-600 bg-gray-700" />
                Bovenaan het blogoverzicht tonen
              </label>
            </Field>
            <Field label="Publicatiedatum">
              <input type="date" value={form.published_at}
                     onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                     className={inputCls} />
              <span className="text-xs text-gray-500">Leeg = datum van publiceren</span>
            </Field>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Lijst ----------
  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Blog</h1>
          <p className="text-gray-400 mt-1">Artikelen op food-flows.com/blog. Alleen beheerbaar door platform-admins.</p>
        </div>
        <button onClick={openNew} data-testid="new-post-btn"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm">
          <Plus className="w-4 h-4" /> Nieuw artikel
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Nog geen artikelen. Maak je eerste artikel aan.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id}
                 className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-start justify-between gap-4 flex-wrap"
                 data-testid={`blog-row-${post.slug}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-white font-semibold">{post.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    post.status === 'published'
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                      : 'bg-gray-600/30 text-gray-300 border-gray-500/40'}`}>
                    {post.status === 'published' ? 'Gepubliceerd' : 'Concept'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                    {(post.language || 'nl').toUpperCase()}
                  </span>
                  {post.featured && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      Uitgelicht
                    </span>
                  )}
                  {post.category && (
                    <span className="text-xs text-gray-400">{post.category}</span>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-1 font-mono">
                  /blog/{post.language}/{post.slug}
                  {post.published_at ? ` · ${post.published_at.slice(0, 10)}` : ''}
                  {post.reading_minutes ? ` · ${post.reading_minutes} min` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {post.status === 'published' && (
                  <a href={`https://food-flows.com/blog/${post.language}/${post.slug}`}
                     target="_blank" rel="noopener noreferrer"
                     className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300" title="Bekijk online">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => openEdit(post.id)} data-testid={`edit-${post.slug}`}
                        className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300" title="Bewerken">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => remove(post)} data-testid={`delete-${post.slug}`}
                        className="p-2 rounded-lg bg-gray-700 hover:bg-red-600 text-red-400 hover:text-white" title="Verwijderen">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inputCls = "w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-600";

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-sm text-gray-400">{label}</label>
    {children}
  </div>
);

// Indicatief voorbeeld in de editor. De echte HTML wordt server-side met
// markdown-it gemaakt bij het opslaan; dit dekt alleen de meest gebruikte
// opmaak zodat de redacteur niet hoeft te publiceren om iets te zien.
function simpleMarkdownPreview(src) {
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc(src || '')
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/^&gt; (.*)$/gm, '<blockquote>$1</blockquote>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>')
    .replace(/^[-*] (.*)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>')
    .split(/\n{2,}/)
    .map(block => /^\s*<(h[1-3]|ul|blockquote)/.test(block) ? block : `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('');
}
